import { InfoPair } from "@/components/info-pair/InfoPair";
import { InvoiceSpecificationInvoiceNumberInput } from "@/components/invoice-specification/components/InvoiceSpecificationInvoiceNumberInput";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { StoreState } from "@/redux/RootReducer";
import {
  NslhCustomerSelectFragment,
  NslhInvoiceByFilterInput,
} from "@/gql/graphql";
import { DateRange, DateRangeDualTextInput } from "@stenajs-webui/calendar";
import { PrimaryButton } from "@stenajs-webui/elements";
import { cssColor } from "@stenajs-webui/theme";
import * as React from "react";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NslhCustomerSearchInput } from "../../components/customer-select/NslhCustomerSearchInput";
import {
  setNslhSearchInvoicesByInvoiceNoData,
  setNslhSearchInvoicesCustomerNoInput,
  setNslhSearchInvoicesCustomerSelectedData,
  setNslhSearchInvoicesDateRangeInput,
  setNslhSearchInvoicesInvoiceNoInput,
} from "../redux/reducers";
import { createNoShowLateHandlingSearchInvoicesStateSelectors } from "../redux/selectors";
import { ZIndex } from "@/lib/utils/ZIndexEnum";

export interface Props {
  handleSearchByCustomerNumberAndDateRange: (
    input: NslhInvoiceByFilterInput
  ) => void;
  handleSearchByInvoiceNumber: (invoiceNumber: string) => void;
}

export const InvoiceSearchRow: React.FC<Props> = ({
  handleSearchByCustomerNumberAndDateRange,
  handleSearchByInvoiceNumber,
}) => {
  const dispatch = useDispatch();

  const selectedCustomer = useSelector((state: StoreState) =>
    createNoShowLateHandlingSearchInvoicesStateSelectors.getNslhSearchInvoicesCustomerSelectedData(
      state
    )
  );

  const invoiceNumber = useSelector((state: StoreState) =>
    createNoShowLateHandlingSearchInvoicesStateSelectors.getNslhSearchInvoicesInvoiceNoInput(
      state
    )
  );

  const loading = useSelector(
    createNoShowLateHandlingSearchInvoicesStateSelectors.getNslhSearchInvoicesLoading
  );

  const dateRangeData = useSelector((state: StoreState): DateRange => {
    const data =
      createNoShowLateHandlingSearchInvoicesStateSelectors.getNslhSearchInvoicesDateRangeInput(
        state
      );
    return {
      startDate: data.startDate ? new Date(data.startDate) : undefined,
      endDate: data.endDate ? new Date(data.endDate) : undefined,
    };
  });

  const setCustomerNumberFromDropDown = useCallback(
    (value: NslhCustomerSelectFragment) => {
      dispatch(setNslhSearchInvoicesCustomerSelectedData(value));
    },
    [dispatch]
  );

  const onCustomerNumberChange = useCallback(
    (custNo: string) => {
      if (!custNo) {
        dispatch(setNslhSearchInvoicesCustomerSelectedData(null));
      }
      dispatch(setNslhSearchInvoicesCustomerNoInput(Number(custNo)));
    },
    [dispatch]
  );

  const onInvoiceNumberChange = useCallback(
    (invoiceNo: string) => {
      dispatch(setNslhSearchInvoicesInvoiceNoInput(invoiceNo));
      dispatch(setNslhSearchInvoicesByInvoiceNoData(undefined));
    },
    [dispatch]
  );

  const searchActiveForByFilter =
    selectedCustomer?.custNo &&
    dateRangeData.startDate &&
    dateRangeData.endDate;

  return (
    <>
      <div className={"invoice_number_input"}>
        <InfoPair
          justifyContent={"unset"}
          alignItems={"center"}
          spacingTopAndBottom={0}
          indentBetweenContent={0.5}
          leftContent={"Invoice number"}
          leftTextWhiteSpace="nowrap"
          leftTextColor={cssColor("--lhds-color-ui-700")}
          leftTextVariant="standard"
          leftTextSize="small"
          leftJustifyContent={"flex-end"}
          indentAfterRightContent={0}
          rightContent={
            <InvoiceSpecificationInvoiceNumberInput
              setInvoiceNumber={onInvoiceNumberChange}
              invoiceNumber={invoiceNumber}
              handleSearchByInvoiceNumber={handleSearchByInvoiceNumber}
              isInternalView
            />
          }
          rightContentUseCustomComponent
          rightWidth={"160px"}
          rightFlexDirection="column"
          borderBottom={false}
        />
      </div>
      <div className={"invoice_search_customer"}>
        <InfoPair
          justifyContent={"unset"}
          alignItems={"center"}
          spacingTopAndBottom={0}
          indentBetweenContent={0.5}
          leftContent={"Customer index or number"}
          leftTextWhiteSpace="nowrap"
          leftTextColor={cssColor("--lhds-color-ui-700")}
          leftTextVariant="standard"
          leftTextSize="small"
          leftJustifyContent={"flex-end"}
          indentAfterRightContent={0}
          rightContent={
            <NslhCustomerSearchInput
              isClearable
              placeholder="Number/index"
              value={selectedCustomer!}
              customerNumber={String(selectedCustomer?.custNo)}
              setCustomerNumber={onCustomerNumberChange}
              onValueChange={setCustomerNumberFromDropDown}
            />
          }
          rightContentUseCustomComponent
          rightWidth={"360px"}
          rightFlexDirection="column"
          borderBottom={false}
        />
      </div>

      <InfoPair
        justifyContent={"unset"}
        alignItems={"center"}
        spacingTopAndBottom={0}
        indentBetweenContent={0.5}
        leftContent={"Months"}
        leftTextWhiteSpace="nowrap"
        leftTextColor={cssColor("--lhds-color-ui-700")}
        leftTextVariant="standard"
        leftTextSize="small"
        leftJustifyContent={"flex-end"}
        indentAfterRightContent={0}
        rightContent={
          <DateRangeDualTextInput
            firstMonthInMonthPicker={new Date(new Date().getFullYear(), 0)}
            value={dateRangeData}
            zIndex={ZIndex.highest}
            onValueChange={(value) =>
              dispatch(
                setNslhSearchInvoicesDateRangeInput({
                  startDate: value.startDate
                    ? value.startDate.toISOString()
                    : undefined,
                  endDate: value.endDate
                    ? value.endDate.toISOString()
                    : undefined,
                })
              )
            }
          />
        }
        rightContentUseCustomComponent
        rightFlexDirection="column"
        borderBottom={false}
      />
      <PrimaryButton
        label={"Search"}
        data-testid={testIdConstants.searchInvoiceSpecificationsButton}
        disabled={!(searchActiveForByFilter || invoiceNumber)}
        loadingLabel={"Search"}
        loading={loading}
        onClick={() => {
          if (invoiceNumber) {
            handleSearchByInvoiceNumber(invoiceNumber);
          } else {
            const { startDate, endDate } = dateRangeData;
            const monthFrom = startDate && startDate.getMonth() + 1;
            const yearFrom = startDate && startDate.getFullYear();
            const monthTo = endDate && endDate.getMonth() + 1;
            const yearTo = endDate && endDate.getFullYear();
            const nslhInvoiceByFilterInput: NslhInvoiceByFilterInput = {
              custNos: [Number(selectedCustomer?.custNo)],
              fromMonth: Number(monthFrom),
              toMonth: Number(monthTo),
              fromYear: Number(yearFrom),
              toYear: Number(yearTo),
            };
            dispatch(
              setNslhSearchInvoicesCustomerSelectedData(selectedCustomer)
            );
            handleSearchByCustomerNumberAndDateRange(nslhInvoiceByFilterInput);
          }
        }}
      />
    </>
  );
};
